import { percentOf } from './money.js';
/* ------------------------------------------------------------------ *
 * Artist codes
 * ------------------------------------------------------------------ */
/**
 * The discount an artist gives their own audience.
 *
 * WHY THIS EXISTS AT ALL. Without it the only thing an artist can post is
 * "please buy my stuff", which carries a social cost — which is exactly why
 * they post it once and then feel awkward. With a code the post becomes "here
 * is 10% off from me": a gift rather than a request. It is the cheapest change
 * available and it changes the social dynamics completely.
 *
 * IT COMES OUT OF OUR MARGIN, NOT THEIR ROYALTY. Royalty is computed per line
 * before any order-level discount (see quote.ts), so a code reduces what the
 * customer pays and leaves what the artist earns untouched. That is not an
 * accident of ordering — it has to stay true, or an artist would be funding
 * their own promotion out of their earnings, and none of them would ever share
 * a code again. There is a test pinning it.
 */
export const ARTIST_COUPON_PERCENT = 10;
/** Caps the giveaway. 10% of a ₹40,000 order is not a thank-you, it is a hole. */
export const ARTIST_COUPON_MAX_DISCOUNT_MINOR = 50_000;
/** Enough that a small order is not subsidised into a loss. */
export const ARTIST_COUPON_MIN_SPEND_MINOR = 50_000;
/**
 * An artist's code is their handle, uppercased.
 *
 * Not a random string: this has to be sayable in a caption and typeable from
 * memory by somebody watching a story. It also tells the buyer whose
 * recommendation they are acting on, which a random code cannot.
 */
export function artistCouponCode(handle) {
    return handle.trim().toUpperCase();
}
export function applyCoupon(input) {
    const { coupon, lines, customerRedemptions = 0, now = Date.now() } = input;
    const refuse = (reason, message) => ({
        ok: false,
        discountMinor: 0,
        reason,
        message,
    });
    if (!coupon)
        return refuse('NOT_FOUND', 'That code is not valid.');
    // A paused or expired coupon gets the same wording as a wrong code. Telling
    // someone a code "has expired" confirms it existed, which is how coupon
    // codes get traded and brute-forced.
    if (coupon.status !== 'ACTIVE')
        return refuse('INACTIVE', 'That code is not valid.');
    if (coupon.validFrom != null && now < coupon.validFrom) {
        return refuse('NOT_STARTED', 'That code is not valid.');
    }
    if (coupon.validUntil != null && now > coupon.validUntil) {
        return refuse('EXPIRED', 'That code has expired.');
    }
    if (coupon.maxRedemptions != null && coupon.redemptions >= coupon.maxRedemptions) {
        return refuse('EXHAUSTED', 'That code has been fully claimed.');
    }
    if (coupon.maxPerCustomer != null && customerRedemptions >= coupon.maxPerCustomer) {
        return refuse('ALREADY_USED', 'You have already used that code.');
    }
    // Only eligible lines count, both toward the minimum spend and toward the
    // discount itself — otherwise a category-restricted coupon could be unlocked
    // by an unrelated item.
    const eligible = coupon.categoryIds?.length
        ? lines.filter((l) => l.categoryIds.some((c) => coupon.categoryIds.includes(c)))
        : lines;
    const eligibleTotal = eligible.reduce((n, l) => n + l.subtotalMinor, 0);
    if (eligibleTotal <= 0) {
        return refuse('NOT_APPLICABLE', 'That code does not apply to anything in your cart.');
    }
    if (coupon.minSpendMinor != null && eligibleTotal < coupon.minSpendMinor) {
        return refuse('BELOW_MIN_SPEND', `Spend a little more to use this code.`);
    }
    let discountMinor = coupon.type === 'PERCENT'
        ? percentOf(eligibleTotal, coupon.value)
        : Math.round(coupon.value);
    if (coupon.maxDiscountMinor != null) {
        discountMinor = Math.min(discountMinor, coupon.maxDiscountMinor);
    }
    // A discount can never exceed what is being discounted, or the order total
    // goes negative and the payment gateway rejects it in a confusing way.
    discountMinor = Math.min(discountMinor, eligibleTotal);
    if (discountMinor <= 0) {
        return refuse('NOT_APPLICABLE', 'That code does not apply to anything in your cart.');
    }
    return { ok: true, discountMinor };
}
export function normaliseCode(code) {
    return code.trim().toUpperCase().replace(/\s+/g, '');
}
//# sourceMappingURL=coupon.js.map